import React, { useState, useEffect } from 'react';
import createAxiosInstance from '../../utils/axiosConfig';
import { update_question_flow } from '../../utils/endpoints';
import Alert from './alerts/alert';
import { useAuth } from '../../context/authContext';

interface QuestionFlowEditSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  questionFlowData: any | null;
  domains: any[];
  onSuccess: () => void;
  onEditQuestions?: (questionFlowId: number) => void;
}

interface QuestionFlowFormData {
  domain_id: number;
  title: string;
  description: string;
  overall_score: number;
  is_enabled: boolean;
  is_deleted: boolean;
}

const QuestionFlowEditSidebar: React.FC<QuestionFlowEditSidebarProps> = ({
  isOpen,
  onClose,
  questionFlowData,
  domains,
  onSuccess,
  onEditQuestions
}) => {
  const { userData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'danger' | 'warning'; show: boolean } | null>(null);
  const [formData, setFormData] = useState<QuestionFlowFormData>({
    domain_id: 0,
    title: '',
    description: '',
    overall_score: 4,
    is_enabled: true,
    is_deleted: false
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Populate form when questionFlowData changes
  useEffect(() => {
    if (questionFlowData) {
      setFormData({
        domain_id: questionFlowData.domain_id || 0,
        title: questionFlowData.title || questionFlowData.name || '',
        description: questionFlowData.description || questionFlowData.shortcode || '',
        overall_score: questionFlowData.overall_score ?? 4,
        is_enabled: questionFlowData.is_enabled ?? true,
        is_deleted: questionFlowData.is_deleted ?? false
      });
    }
  }, [questionFlowData]);

  // Form validation
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.domain_id) {
      newErrors.domain_id = 'Please select a domain';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Shortcode is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (name === 'is_deleted' ? checked : checked) : (name === 'domain_id' || name === 'overall_score' ? parseInt(value) : value)
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm() || !questionFlowData?.id) return;
    
    setLoading(true);
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.put(update_question_flow(questionFlowData.id), formData);
      
      if (response.status === 200 || response.status === 201) {
        setAlert({
          message: 'Question flow updated successfully!',
          type: 'success',
          show: true
        });
        
        // Call onSuccess after a short delay to show the success message
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error updating question flow:', error);
      setAlert({
        message: 'Error updating question flow. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      domain_id: 0,
      title: '',
      description: '',
      overall_score: 4,
      is_enabled: true,
      is_deleted: false
    });
    setErrors({});
    setAlert(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="offcanvas-backdrop fade show"
        onClick={handleClose}
      ></div>
      <div
        className="offcanvas offcanvas-end show"
        tabIndex={-1}
        style={{ 
          zIndex: 1050, 
          width: '40vw',
          maxWidth: '500px'
        }}
      >
        <div className="offcanvas-header" style={{backgroundColor: 'white', borderBottom: '1px solid #dee2e6'}}>
          <h5 className="offcanvas-title" style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: '#183FCE'
          }}>
            Edit Question Flow
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={handleClose}
          ></button>
        </div>
        
        <div className="offcanvas-body">
          <div className="mb-3">
            <label htmlFor="flowDomain" className="form-label">Select Domain:</label>
            <select
              className={`form-select bg-white ${errors.domain_id ? 'is-invalid' : ''}`}
              id="flowDomain"
              name="domain_id"
              value={formData.domain_id}
              onChange={handleChange}
              disabled={loading}
            >
              <option value={0}>Select a domain</option>
              {domains.map((domain, index) => (
                <option key={domain.id || index} value={domain.id || index + 1}>
                  {domain.title || domain.name || `Domain ${index + 1}`}
                </option>
              ))}
            </select>
            {errors.domain_id && (
              <div className="invalid-feedback">{errors.domain_id}</div>
            )}
          </div>
          
          <div className="mb-3">
            <label htmlFor="flowTitle" className="form-label">Title:</label>
            <input
              type="text"
              className={`form-control bg-white ${errors.title ? 'is-invalid' : ''}`}
              id="flowTitle"
              name="title"
              value={formData.title}
              onChange={handleChange}
              disabled={loading}
            />
            {errors.title && (
              <div className="invalid-feedback">{errors.title}</div>
            )}
          </div>
          
          <div className="mb-3">
            <label htmlFor="flowDescription" className="form-label">Shortcode:</label>
            <input
              type="text"
              className={`form-control bg-white ${errors.description ? 'is-invalid' : ''}`}
              id="flowDescription"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={loading}
            />
            {errors.description && (
              <div className="invalid-feedback d-block text-danger" style={{fontSize: '1rem'}}>{errors.description}</div>
            )}
          </div>
          
          <div className="mb-3">
            <label htmlFor="flowOverallScore" className="form-label">Overall Score:</label>
            <input
              type="number"
              className="form-control bg-white"
              id="flowOverallScore"
              name="overall_score"
              min="0"
              step="1"
              value={formData.overall_score}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
          
          {(userData?.is_admin === 1 || userData?.account_type === 5) && questionFlowData?.id && (
            <div className="mb-3">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none"
                onClick={() => {
                  if (onEditQuestions && questionFlowData.id) {
                    onClose();
                    onEditQuestions(questionFlowData.id);
                  }
                }}
                disabled={loading}
              >
                Edit Questions
              </button>
            </div>
          )}
          
          <div className="mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="flowEnabled"
                name="is_enabled"
                checked={formData.is_enabled}
                onChange={handleChange}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="flowEnabled">
                Enable Question Flow
              </label>
            </div>
          </div>
          
          <div className="mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="flowDeleted"
                name="is_deleted"
                checked={formData.is_deleted}
                onChange={handleChange}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="flowDeleted">
                Mark as Deleted
              </label>
            </div>
          </div>
          
          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-light"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-rounded"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Updating...
                </div>
              ) : (
                'Update Question Flow'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <Alert
          message={alert.message}
          type={alert.type}
          show={alert.show}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default QuestionFlowEditSidebar; 